/**
 * Shopify Custom Magnet Cropper Logic
 * Handles file upload, interactive cropping, and cart preparation.
 */

class MagnetApp {
    constructor(containerId, options = {}) {
        this.container = document.getElementById(containerId);
        // Default Global Settings (used for initial state)
        this.defaults = {
            aspectRatio: 27 / 43,
            outputWidth: 810, // 27 * 30 (High res print quality)
            outputHeight: 1290, // 43 * 30
            maxImages: 50,
            ...options
        };

        this.files = []; // Array of MagnetImage objects
        this.currentEditIndex = -1;

        this.initUI();

        // Read Dynamic Price/Currency from Container Data Attributes
        this.productPrice = parseFloat(this.container.dataset.price) || 10.00;
        this.currencySymbol = this.container.dataset.currency || '$';

        // Initial Stat Update
        this.updateStats();
    }

    initUI() {
        // Create Main Structure
        this.container.innerHTML = `
            <div class="magnet-uploader-area" id="drop-zone">
                <img src="https://cdn.shopify.com/s/files/1/0699/2763/3117/files/imageupload.png?v=1767486704" style="width: 100%;">
                <p>Drag & Drop your photos here</p>
                <p class="subtext">or click to browse</p>
                <button class="magnet-upload-btn select-photos">Select Photos</button>
                <input type="file" multiple accept="image/*" style="display:none" id="file-input">
            </div>
            
            <div class="magnet-gallery-grid" id="gallery-grid"></div>

            <div class="magnet-footer-bar">
                <div id="magnet-count">0 items selected</div>
                <div class="magnet-price-total">Total: ${this.container.dataset.currency || '$'}0.00</div>
                <button class="magnet-upload-btn full-width" id="add-to-cart-btn">Add to Cart</button>
            </div>

            <!-- Cropper Modal -->
            <div class="magnet-cropper-modal" id="cropper-modal">
                <div class="magnet-cropper-content">
                    <div class="magnet-modal-header">
                        <h3>Adjust Image</h3>
                        <div class="magnet-orientation-toggles">
                            <button class="magnet-toggle active" data-ratio="portrait">Portrait</button>
                            <button class="magnet-toggle" data-ratio="landscape">Landscape</button>
                        </div>
                    </div>
                    <div class="magnet-canvas-wrapper" id="canvas-wrapper">
                        <canvas id="crop-canvas"></canvas>
                    </div>
                    <div class="magnet-controls">
                        <div class="magnet-slider-group">
                            <span class="icon-minus">-</span>
                            <input type="range" min="1" max="3" step="0.1" value="1" id="zoom-slider">
                            <span class="icon-plus">+</span>
                        </div>
                        <div class="magnet-actions">
                            <button class="magnet-action-btn magnet-btn-cancel" id="crop-cancel">Cancel</button>
                            <button class="magnet-action-btn magnet-btn-save" id="crop-save">Done</button>
                        </div>
                    </div>
                </div>
            </div>
        `;

        // Create Main Structure (Events)
        this.fileInput = this.container.querySelector('#file-input');
        this.dropZone = this.container.querySelector('#drop-zone');
        this.gallery = this.container.querySelector('#gallery-grid');
        this.modal = this.container.querySelector('#cropper-modal');
        this.canvas = this.container.querySelector('#crop-canvas');
        this.ctx = this.canvas.getContext('2d');
        this.zoomSlider = this.container.querySelector('#zoom-slider');

        // Orientation Toggles
        this.container.querySelectorAll('.magnet-toggle').forEach(btn => {
            btn.onclick = (e) => this.setOrientation(e.target.dataset.ratio, e.target);
        });

        // Upload Triggers
        this.container.querySelector('.magnet-upload-btn').onclick = () => this.fileInput.click();
        this.container.querySelector('#add-to-cart-btn').onclick = () => this.addToCart();
        this.fileInput.onchange = (e) => this.handleFiles(e.target.files);

        // DRAG AND DROP FIX
        ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
            this.dropZone.addEventListener(eventName, (e) => {
                e.preventDefault();
                e.stopPropagation();
            }, false);
        });

        ['dragenter', 'dragover'].forEach(eventName => {
            this.dropZone.addEventListener(eventName, () => {
                this.dropZone.classList.add('highlight');
            }, false);
        });

        ['dragleave', 'drop'].forEach(eventName => {
            this.dropZone.addEventListener(eventName, () => {
                this.dropZone.classList.remove('highlight');
            }, false);
        });

        this.dropZone.addEventListener('drop', (e) => {
            const dt = e.dataTransfer;
            const files = dt.files;
            this.handleFiles(files);
        }, false);


        // Modal Actions
        this.container.querySelector('#crop-cancel').onclick = () => this.closeModal();
        this.container.querySelector('#crop-save').onclick = () => this.saveCrop();
        this.zoomSlider.oninput = (e) => this.updateEditorZoom(parseFloat(e.target.value));

        // Canvas Interaction
        this.isDragging = false;
        this.dragStart = { x: 0, y: 0 };
        this.canvas.addEventListener('mousedown', this.startDrag.bind(this));
        window.addEventListener('mousemove', this.doDrag.bind(this));
        window.addEventListener('mouseup', this.endDrag.bind(this));

        // Touch support
        this.canvas.addEventListener('touchstart', (e) => {
            e.preventDefault();
            this.startDrag(e.touches[0]);
        }, { passive: false });
        window.addEventListener('touchmove', (e) => {
            if (this.isDragging) {
                e.preventDefault();
                this.doDrag(e.touches[0]);
            }
        }, { passive: false });
        window.addEventListener('touchend', this.endDrag.bind(this));
    }

    setOrientation(mode, btn) {
        if (this.currentEditIndex === -1) return;

        // Update active class UI
        this.container.querySelectorAll('.magnet-toggle').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');

        // Update CURRENT ITEM settings (Link data to the object, NOT global)
        const magnet = this.files[this.currentEditIndex];

        if (mode === 'landscape') {
            magnet.aspectRatio = 43 / 27;
            magnet.outputWidth = 1290;
            magnet.outputHeight = 810;
            magnet.orientation = 'landscape';
        } else {
            magnet.aspectRatio = 27 / 43;
            magnet.outputWidth = 810;
            magnet.outputHeight = 1290;
            magnet.orientation = 'portrait';
        }

        // Force Re-Init of Editor to calculate new CropBox and reset image
        this.initEditorState(this.currentEditIndex, true);
    }

    handleFiles(files) {
        if (!files || files.length === 0) return;

        // NEW: Max file size (20MB)
        const MAX_SIZE = 20 * 1024 * 1024;

        Array.from(files).forEach(file => {
            // 1. Validate Type
            if (!file.type.match('image.*')) {
                alert(`Skipped "${file.name}": Only image files are allowed.`);
                return;
            }

            // 2. Validate Size
            if (file.size > MAX_SIZE) {
                alert(`Skipped "${file.name}": File size exceeds 20MB limit.`);
                return;
            }

            if (this.files.length >= this.defaults.maxImages) return;

            const reader = new FileReader();
            reader.onload = (e) => {
                const img = new Image();
                img.onload = () => {
                    this.addMagnetImage(file, img);
                };
                img.src = e.target.result;
            };
            reader.readAsDataURL(file);
        });
    }

    addMagnetImage(file, imgObj) {
        // PER-IMAGE STATE INITIALIZATION
        // Inspect the image dimensions to guess best default orientation
        // If image is Wider than Tall, default to Landscape.
        // Else default to Portrait.

        const isLandscape = imgObj.width > imgObj.height;
        const initialOrientation = isLandscape ? 'landscape' : 'portrait';

        const magnet = {
            id: Date.now() + Math.random(),
            file: file,
            originalImg: imgObj,
            previewSrc: imgObj.src, // Initially full image
            scale: 1,
            pan: { x: 0, y: 0 },
            isCropped: false,
            needsCrop: true, // NEW: Flag to show warning until edited

            // INDIVIDUAL SETTINGS
            orientation: initialOrientation,
            aspectRatio: isLandscape ? (43 / 27) : (27 / 43),
            outputWidth: isLandscape ? 1290 : 810,
            outputHeight: isLandscape ? 810 : 1290
        };

        this.files.push(magnet);
        this.renderGallery();
        this.updateStats();
    }

    renderGallery() {
        this.gallery.innerHTML = '';
        this.files.forEach((magnet, index) => {
            const card = document.createElement('div');
            // FIX: Add dynamic class 'landscape' or 'portrait'
            card.className = `magnet-thumb-card ${magnet.isCropped ? '' : 'needs-crop'} ${magnet.orientation || 'portrait'}`;

            const img = document.createElement('img');
            img.src = magnet.previewSrc;

            // NEW: Crop Warning Badge
            if (magnet.needsCrop) {
                const badge = document.createElement('div');
                badge.className = 'magnet-crop-warning';
                badge.innerHTML = '❗️ Crop Needed';
                card.appendChild(badge);
            }

            const removeBtn = document.createElement('div');
            removeBtn.className = 'magnet-remove-btn';
            removeBtn.innerHTML = '&times;';
            removeBtn.onclick = (e) => {
                e.stopPropagation();
                this.removeImage(index);
            };

            card.appendChild(img);
            card.appendChild(removeBtn);

            card.onclick = () => this.openEditor(index);

            this.gallery.appendChild(card);
        });
    }

    removeImage(index) {
        // Close modal if deleting the currently edited image
        if (this.currentEditIndex === index) {
            this.closeModal();
        } else if (this.currentEditIndex > index) {
            // Adjust index if we deleted one before it
            this.currentEditIndex--;
        }

        this.files.splice(index, 1);
        this.renderGallery();
        this.updateStats();
    }

    updateStats() {
        const count = this.files.length;
        document.getElementById('magnet-count').textContent = `${count} items selected`;

        // Dynamic price calculation
        const total = (count * this.productPrice).toFixed(2);
        document.querySelector('.magnet-price-total').textContent = `Total: ${this.currencySymbol}${total}`;
    }

    // ==========================================
    // EDITOR LOGIC
    // ==========================================

    openEditor(index) {
        this.currentEditIndex = index;
        const magnet = this.files[index];

        this.modal.classList.add('active');

        // Sync Buttons to CURRENT item's orientation
        const btns = this.container.querySelectorAll('.magnet-toggle');
        btns.forEach(b => {
            if (b.dataset.ratio === magnet.orientation) {
                b.classList.add('active');
            } else {
                b.classList.remove('active');
            }
        });

        // Use a timeout to allow modal to display before calculating sizes
        setTimeout(() => this.initEditorState(index, false), 50);
    }

    closeModal() {
        // NEW: Clear warning flag when returning from modal (Cancel or Done)
        if (this.currentEditIndex !== -1 && this.files[this.currentEditIndex]) {
            this.files[this.currentEditIndex].needsCrop = false;
        }

        this.modal.classList.remove('active');
        this.currentEditIndex = -1;
        this.renderGallery(); // Re-render to remove badge
    }

    initEditorState(index, forceReset) {
        const magnet = this.files[index];
        const img = magnet.originalImg;
        const wrapper = document.getElementById('canvas-wrapper');

        // Canvas size = Full Wrapper Size (Viewport)
        this.canvas.width = wrapper.clientWidth;
        this.canvas.height = wrapper.clientHeight;

        // Calculate CropBox (Centered, Max Fit) BASED ON ITEM ASPECT RATIO
        const padding = 20;
        const availW = this.canvas.width - (padding * 2);
        const availH = this.canvas.height - (padding * 2);

        let cropW = availW;
        let cropH = availW / magnet.aspectRatio; // USE ITEM CONFIG, NOT GLOBAL

        if (cropH > availH) {
            cropH = availH;
            cropW = availH * magnet.aspectRatio;
        }

        this.cropBox = {
            x: (this.canvas.width - cropW) / 2,
            y: (this.canvas.height - cropH) / 2,
            w: cropW,
            h: cropH
        };

        // Initialize State
        this.editState = {
            scale: magnet.scale || 1,
            pan: { ...magnet.pan },
            minScale: 1
        };

        // Calculate min scale to "cover" the cropBox
        const scaleX = this.cropBox.w / img.width;
        const scaleY = this.cropBox.h / img.height;
        this.editState.minScale = Math.max(scaleX, scaleY);

        // Reset if first time OR forceReset (orientation change)
        if (!magnet.isCropped || forceReset) {
            this.editState.scale = this.editState.minScale;
            // Center image in CropBox
            this.editState.pan.x = this.cropBox.x + (this.cropBox.w - img.width * this.editState.scale) / 2;
            this.editState.pan.y = this.cropBox.y + (this.cropBox.h - img.height * this.editState.scale) / 2;
        }

        // Update Slider
        this.zoomSlider.min = this.editState.minScale;
        this.zoomSlider.max = this.editState.minScale * 3;
        // Fix: Ensure value is within bounds (in case orientation swap changed minScale)
        if (this.editState.scale < this.editState.minScale) this.editState.scale = this.editState.minScale;

        this.zoomSlider.value = this.editState.scale;
        this.zoomSlider.step = (this.editState.minScale * 3 - this.editState.minScale) / 100;

        this.drawEditor();
    }

    drawEditor() {
        if (this.currentEditIndex === -1 || !this.editState) return;
        const magnet = this.files[this.currentEditIndex];
        const img = magnet.originalImg;

        // 1. Clear Full Canvas
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        this.ctx.fillStyle = '#111';
        this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

        // 2. Draw Image (Transformed)
        this.ctx.drawImage(
            img,
            this.editState.pan.x,
            this.editState.pan.y,
            img.width * this.editState.scale,
            img.height * this.editState.scale
        );

        // 3. Draw Overlay (Darken outside CropBox)
        this.ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';

        // Top rect
        this.ctx.fillRect(0, 0, this.canvas.width, this.cropBox.y);
        // Bottom rect
        this.ctx.fillRect(0, this.cropBox.y + this.cropBox.h, this.canvas.width, this.canvas.height - (this.cropBox.y + this.cropBox.h));
        // Left rect
        this.ctx.fillRect(0, this.cropBox.y, this.cropBox.x, this.cropBox.h);
        // Right rect
        this.ctx.fillRect(this.cropBox.x + this.cropBox.w, this.cropBox.y, this.canvas.width - (this.cropBox.x + this.cropBox.w), this.cropBox.h);

        // 4. Draw Crop Border
        this.ctx.strokeStyle = 'white';
        this.ctx.lineWidth = 2;
        this.ctx.strokeRect(this.cropBox.x, this.cropBox.y, this.cropBox.w, this.cropBox.h);
    }

    updateEditorZoom(newScale) {
        if (this.currentEditIndex === -1) return;

        const oldScale = this.editState.scale;

        // Zoom center = CropBox Center
        const cx = this.cropBox.x + this.cropBox.w / 2;
        const cy = this.cropBox.y + this.cropBox.h / 2;

        // Image Point at Center
        const imgX = (cx - this.editState.pan.x) / oldScale;
        const imgY = (cy - this.editState.pan.y) / oldScale;

        this.editState.scale = newScale;

        // New pan to keep Image Point at Center
        this.editState.pan.x = cx - imgX * newScale;
        this.editState.pan.y = cy - imgY * newScale;

        this.constrainPan();
        this.drawEditor();
    }

    startDrag(e) {
        const clientX = e.clientX || e.touches[0].clientX;
        const clientY = e.clientY || e.touches[0].clientY;

        this.isDragging = true;
        this.dragStart = { x: clientX, y: clientY };
        this.dragStartPan = { ...this.editState.pan };
    }

    doDrag(e) {
        if (!this.isDragging) return;
        const clientX = e.clientX || e.touches[0].clientX;
        const clientY = e.clientY || e.touches[0].clientY;

        const dx = clientX - this.dragStart.x;
        const dy = clientY - this.dragStart.y;

        this.editState.pan.x = this.dragStartPan.x + dx;
        this.editState.pan.y = this.dragStartPan.y + dy;

        this.constrainPan();
        this.drawEditor();
    }

    endDrag() {
        this.isDragging = false;
    }

    constrainPan() {
        const imgW = this.files[this.currentEditIndex].originalImg.width * this.editState.scale;
        const imgH = this.files[this.currentEditIndex].originalImg.height * this.editState.scale;

        // Constraint: Image must cover CropBox
        // Image Left (pan.x) must be <= CropBox Left (cropBox.x)
        if (this.editState.pan.x > this.cropBox.x) {
            this.editState.pan.x = this.cropBox.x;
        }

        // Image Top (pan.y) must be <= CropBox Top (cropBox.y)
        if (this.editState.pan.y > this.cropBox.y) {
            this.editState.pan.y = this.cropBox.y;
        }

        // Image Right (pan.x + imgW) must be >= CropBox Right (cropBox.x + cropBox.w)
        if (this.editState.pan.x + imgW < this.cropBox.x + this.cropBox.w) {
            this.editState.pan.x = (this.cropBox.x + this.cropBox.w) - imgW;
        }

        // Image Bottom (pan.y + imgH) must be >= CropBox Bottom (cropBox.y + cropBox.h)
        if (this.editState.pan.y + imgH < this.cropBox.y + this.cropBox.h) {
            this.editState.pan.y = (this.cropBox.y + this.cropBox.h) - imgH;
        }
    }

    saveCrop() {
        const magnet = this.files[this.currentEditIndex];

        // Save state 
        magnet.scale = this.editState.scale;
        magnet.pan = { ...this.editState.pan };

        // Calculate Crop Parameters relative to Image Source
        const cropX_in_canvas = this.cropBox.x;
        const cropY_in_canvas = this.cropBox.y;

        // Image TopLeft is at pan.x, pan.y
        const sourceX = (cropX_in_canvas - this.editState.pan.x) / this.editState.scale;
        const sourceY = (cropY_in_canvas - this.editState.pan.y) / this.editState.scale;
        const sourceW = this.cropBox.w / this.editState.scale;
        const sourceH = this.cropBox.h / this.editState.scale;

        this.generateCropBlob(magnet, sourceX, sourceY, sourceW, sourceH, (blob, dataUrl) => {
            magnet.blob = blob;
            magnet.previewSrc = dataUrl;
            magnet.isCropped = true;
            this.closeModal();
            this.renderGallery();
        });
    }

    generateCropBlob(magnet, sx, sy, sw, sh, callback) {
        const img = magnet.originalImg;
        const canvas = document.createElement('canvas');
        // USE ITEM CONFIG, NOT GLOBAL SETTINGS
        canvas.width = magnet.outputWidth || this.defaults.outputWidth;
        canvas.height = magnet.outputHeight || this.defaults.outputHeight;
        const ctx = canvas.getContext('2d');

        // Draw clipped image
        ctx.drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);

        // Callback
        canvas.toBlob((blob) => {
            const url = canvas.toDataURL('image/jpeg', 0.85);
            callback(blob, url);
        }, 'image/jpeg', 0.85);
    }

    async addToCart() {
        if (this.files.length === 0) {
            alert("Please select at least one image.");
            return;
        }

        const addBtn = document.getElementById('add-to-cart-btn');
        const originalText = addBtn.textContent;
        addBtn.textContent = 'Processing Images...';
        addBtn.disabled = true;

        try {
            // 1. Process/Crop all files
            const processedBlobs = [];
            for (let i = 0; i < this.files.length; i++) {
                const magnet = this.files[i];
                if (!magnet.blob) {
                    await new Promise(resolve => {
                        const img = magnet.originalImg;
                        // USE ITEM CONFIG IF AVAILABLE, ELSE DEFAULT
                        const outW = magnet.outputWidth || this.defaults.outputWidth;
                        const outH = magnet.outputHeight || this.defaults.outputHeight;

                        // Calculate standard center crop (Source Coordinates)
                        const imgAspect = img.width / img.height;
                        const targetAspect = outW / outH;

                        let sw, sh, sx, sy;

                        if (imgAspect > targetAspect) {
                            // Image is wider than target: constrain height
                            sh = img.height;
                            sw = img.height * targetAspect;
                            sy = 0;
                            sx = (img.width - sw) / 2;
                        } else {
                            // Image is taller than target: constrain width
                            sw = img.width;
                            sh = img.width / targetAspect;
                            sx = 0;
                            sy = (img.height - sh) / 2;
                        }

                        this.generateCropBlob(magnet, sx, sy, sw, sh, (blob, url) => {
                            magnet.blob = blob;
                            magnet.previewSrc = url;
                            resolve();
                        });
                    });
                }
                processedBlobs.push(magnet.blob);
            }

            addBtn.textContent = 'Uploading...';

            // 2. Upload Files (REAL BACKEND)
            addBtn.textContent = 'Uploading...';
            const BACKEND_URL = 'https://magvercel-seven.vercel.app';
            const imageUrls = [];

            // Generate a Unique Batch ID for this set of uploads (e.g., "X7K9P2")
            // Generate or Retrieve a consistent Batch ID for this session
            // distinct from "per upload" to group entire cart sessions together
            let batchId = sessionStorage.getItem('magnet_batch_id');
            if (!batchId) {
                batchId = Math.random().toString(36).substring(2, 8).toUpperCase();
                sessionStorage.setItem('magnet_batch_id', batchId);
            }

            for (let i = 0; i < processedBlobs.length; i++) {
                const blob = processedBlobs[i];
                // New Format: magnet_[BATCH_ID]_[TIMESTAMP]_[INDEX].jpg
                const filename = `magnet_${batchId}_${Date.now()}_${i}.jpg`;
                addBtn.textContent = `Uploading ${i + 1}/${processedBlobs.length}...`;

                // A. Get Upload Target (with Retry)
                let signRes;
                let attempts = 0;
                while (attempts < 3) {
                    try {
                        signRes = await fetch(`${BACKEND_URL}/api/sign-upload`, {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                                filename: filename,
                                filetype: 'image/jpeg',
                                filesize: blob.size
                            })
                        });
                        if (signRes.ok) break; // Success
                    } catch (e) {
                        console.warn(`Upload Sign Attempt ${attempts + 1} failed:`, e);
                    }
                    attempts++;
                    if (attempts < 3) await new Promise(r => setTimeout(r, 1000)); // Wait 1s
                }

                if (!signRes || !signRes.ok) throw new Error('Failed to get upload target after 3 attempts');
                const signData = await signRes.json();

                // B. Upload to Shopify Staged Target
                const formData = new FormData();
                // Parameters usually need to be added first
                for (const param of signData.parameters) {
                    formData.append(param.name, param.value);
                }
                formData.append('file', blob, filename);

                const uploadRes = await fetch(signData.targetUrl, {
                    method: 'POST',
                    body: formData
                });
                if (!uploadRes.ok) throw new Error('Failed to upload file to storage');

                // C. Register File to make it permanent
                const regRes = await fetch(`${BACKEND_URL}/api/register-file`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        resourceUrl: signData.resourceUrl,
                        filename: filename
                    })
                });

                if (!regRes.ok) {
                    console.warn("Registration failed.");
                } else {
                    const regData = await regRes.json();
                    let finalUrl = regData.url;

                    // If URL is missing, poll for it (processing state)
                    if (!finalUrl && regData.id) {
                        addBtn.textContent = `Processing ${i + 1}/${processedBlobs.length}...`;
                        for (let attempt = 0; attempt < 10; attempt++) {
                            await new Promise(r => setTimeout(r, 1000));
                            try {
                                const pollRes = await fetch(`${BACKEND_URL}/api/poll-file`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({ id: regData.id })
                                });
                                if (pollRes.ok) {
                                    const pollData = await pollRes.json();
                                    if (pollData.url) {
                                        finalUrl = pollData.url;
                                        break;
                                    }
                                }
                            } catch (e) {
                                console.error("Polling error", e);
                            }
                        }
                    }

                    if (finalUrl) {
                        imageUrls.push(finalUrl);
                    } else {
                        // Fallback if creates failed or timed out
                        imageUrls.push(regData.id || "Error: Image Processing Failed");
                    }
                }
            }


            addBtn.textContent = 'Adding to Cart...';

            // 3. Add to Cart (Shopify AJAX API)
            // We need the Variant ID.
            // Try to find it from the DOM or URL.
            // Assumption: The liquid template renders a standard product form or we grab the first available variant.
            let variantId = null;

            // Try standard selector
            const variantInput = document.querySelector('[name="id"]');
            if (variantInput) {
                variantId = variantInput.value;
            } else {
                // Fallback: If not found, use window.meta (if available) or alert
                // For the dev store, we might need to hardcode or ask user to select.
                // Let's rely on standard Shopify `window.ShopifyAnalytics.meta.selectedVariantId` if exists
                // OR fetch the product.json
                try {
                    const path = window.location.pathname;
                    const handle = path.split('/').pop();
                    const pJson = await fetch(`/products/${handle}.js`).then(r => r.json());
                    variantId = pJson.variants[0].id;
                } catch (e) {
                    console.error("Could not determine variant ID", e);
                    alert("Error: Could not determine product variant.");
                    addBtn.disabled = false;
                    addBtn.textContent = originalText;
                    return;
                }
            }

            // NEW: Batch ID is already retrieved at top of function.
            // const batchId = sessionStorage.getItem('magnet_batch_id');

            // Create items array
            const items = imageUrls.map(url => {
                return {
                    id: variantId,
                    quantity: 1,
                    properties: {
                        'Uploaded Image': url,
                        // NEW: Hidden Property for tracking
                        '_BatchID': batchId
                    }
                };
            });

            console.log("Adding items:", items);

            const response = await fetch('/cart/add.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ items: items })
            });

            const data = await response.json();

            if (response.ok) {
                // Success
                window.location.href = '/cart';
            } else {
                // Error
                console.error("Cart Error:", data);
                alert("Failed to add to cart:\n" + (data.description || data.message || JSON.stringify(data)));
                addBtn.disabled = false;
                addBtn.textContent = originalText;
            }

        } catch (error) {
            console.error(error);
            alert("An error occurred: " + error.message);
            addBtn.disabled = false;
            addBtn.textContent = originalText;
        }
    }

    closeModal() {
        // NEW: Clear warning flag when returning from modal (Cancel or Done)
        if (this.currentEditIndex !== -1 && this.files[this.currentEditIndex]) {
            this.files[this.currentEditIndex].needsCrop = false;
        }

        this.modal.classList.remove('active');
        this.currentEditIndex = -1;
        this.renderGallery(); // Re-render to remove badge
    }
}

// Initialize
document.addEventListener('DOMContentLoaded', () => {
    window.magnetApp = new MagnetApp('magnet-app');
});
